import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CourseDesign,
  DesignDocument,
  DesignRun,
  RealizationTarget,
  designRunsService,
} from '../../../services/designRunsService';
import { Chip, SmallButton } from './editors';
import { BriefTab } from './BriefTab';
import { OverviewTab } from './OverviewTab';
import { DossierAssets } from './DossierAssets';
import { DossierLesson } from './DossierLesson';

const TARGET_META: Record<RealizationTarget, { icon: string; name: string }> = {
  simon: { icon: '🎬', name: 'Simon (OpenSesame)' },
  talaera: { icon: '🎓', name: 'Talaera platform' },
};

function TargetIcons({ run, onOpenRender }: { run: DesignRun; onOpenRender: () => void }) {
  const entries = (Object.keys(TARGET_META) as RealizationTarget[])
    .filter((t) => run.realizations?.[t]?.status);
  if (entries.length === 0) return null;
  return (
    <div className="flex gap-1.5">
      {entries.map((t) => {
        const r = run.realizations![t]!;
        const stale = (r.design_version ?? 0) < run.design_version;
        const done = r.status === 'package_ready' || r.status === 'building';
        const tone = stale ? 'bg-amber-50 border-amber-200'
          : r.status === 'failed' ? 'bg-red-50 border-red-200'
          : done ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200';
        return (
          <button key={t} onClick={onOpenRender} title={`${TARGET_META[t].name}: ${stale ? 'stale' : r.status}`}
            className={`px-1.5 py-0.5 rounded border text-sm ${tone}`}>
            {TARGET_META[t].icon}
          </button>
        );
      })}
    </div>
  );
}

const ACTIVE = ['created', 'designing'];

/** Extract the flagged word from a vocabulary-gate validation message
    (`… uses "niceness" (off-list, above B1-B2) …` / `… "candid" is C1 …`). */
function vocabIssueWord(issue: string): string | null {
  const match = /(?:uses|:)?\s*"([^"]+)"\s+(?:is\s+)?\((?:off-list|[ABC][12])/.exec(issue)
    ?? /uses "([^"]+)"/.exec(issue)
    ?? /"([^"]+)" is (?:off-list|[ABC][12])/.exec(issue);
  return match ? match[1] : null;
}

const STATUS_LABELS: Record<string, { label: string; tone: 'gray' | 'blue' | 'amber' | 'green' | 'red' }> = {
  created: { label: 'Starting…', tone: 'blue' },
  designing: { label: 'Designing…', tone: 'blue' },
  design_review: { label: 'Ready for your review', tone: 'amber' },
  approved: { label: 'Approved', tone: 'green' },
  design_failed: { label: 'Design failed', tone: 'red' },
};

type Tab = 'brief' | 'overview' | 'lessons' | 'media';

function DesigningProgress({ design }: { design: CourseDesign | null }) {
  const syllabusLessons = design?.syllabus?.lessons ?? [];
  const designed = new Set((design?.lessons ?? []).map((l) => l.lesson_number));
  return (
    <div className="p-4 bg-white border border-gray-200 rounded-md space-y-1.5">
      <div className="flex items-center gap-2 text-sm text-gray-700">
        <span className={syllabusLessons.length ? 'text-green-600' : 'animate-pulse text-blue-600'}>
          {syllabusLessons.length ? '✓' : '●'}
        </span>
        Course syllabus{syllabusLessons.length ? ` — ${syllabusLessons.length} lessons planned` : '…'}
      </div>
      {syllabusLessons.map((l) => (
        <div key={l.lesson_number} className="flex items-center gap-2 text-sm text-gray-700 ml-1">
          <span className={
            designed.has(l.lesson_number) ? 'text-green-600'
              : designed.size + 1 === l.lesson_number ? 'animate-pulse text-blue-600' : 'text-gray-300'
          }>
            {designed.has(l.lesson_number) ? '✓' : designed.size + 1 === l.lesson_number ? '●' : '○'}
          </span>
          Lesson {l.lesson_number} — {l.title}
        </div>
      ))}
    </div>
  );
}

export function DesignRunPage({ runId, courseTitle, onBack, onOpenRender }: {
  runId: string;
  courseTitle: string;
  onBack: () => void;
  onOpenRender: () => void;
}) {
  const [approveMenu, setApproveMenu] = useState(false);
  const [run, setRun] = useState<DesignRun | null>(null);
  const [doc, setDoc] = useState<DesignDocument | null>(null);
  const [draft, setDraft] = useState<CourseDesign | null>(null);
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState<Tab>('overview');
  const [issues, setIssues] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const nextRun = await designRunsService.get(runId);
      setRun(nextRun);
      const nextDoc = await designRunsService.getDocument(runId);
      setDoc(nextDoc);
      setDraft((prev) => (prev && editing ? prev : nextDoc.design));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [runId, editing]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (!run) return;
    const active = ACTIVE.includes(run.design_status)
      || Object.values(run.realizations ?? {}).some(
        (r) => r?.status === 'realizing' || r?.status === 'building');
    if (!active) return;
    const timer = setInterval(refresh, 5000);
    return () => clearInterval(timer);
  }, [run, refresh]);

  const totalCost = useMemo(
    () => (run?.stages ?? []).reduce((s, st) => s + (Number(st.total_cost_usd) || 0), 0),
    [run],
  );

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    setIssues([]);
    try {
      await fn();
      await refresh();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const saveDraft = () => act(async () => {
    if (!draft) return;
    const result = await designRunsService.putDesign(runId, draft);
    if (result.issues?.length) {
      setIssues(result.issues);
      throw new Error('The design has validation issues — fix them and save again.');
    }
    setEditing(false);
  });

  // The vocabulary gate is overridable per course: the subject matter may
  // legitimately need above-level words ("candid" in a Radical Candor course).
  const allowWord = (word: string) => act(async () => {
    const base = (editing ? draft : doc?.design) ?? null;
    if (!base) return;
    const exceptions = base.scope.vocab_exceptions ?? [];
    if (exceptions.includes(word)) return;
    const next = {
      ...base,
      scope: { ...base.scope, vocab_exceptions: [...exceptions, word] },
    };
    const result = await designRunsService.putDesign(runId, next);
    if (result.issues?.length) {
      setIssues(result.issues);
      if (editing) setDraft(next);
      return; // other issues remain — keep the banner, exception is saved in the draft
    }
    setIssues([]);
    if (editing) { setDraft(next); setEditing(false); }
  });

  if (!run) return <div className="p-6 text-sm text-gray-500">Loading…</div>;
  const status = STATUS_LABELS[run.design_status] ?? { label: run.design_status, tone: 'gray' as const };
  const isDesigning = ACTIVE.includes(run.design_status);
  const design = editing ? draft : (doc?.design ?? null);
  const lessonNumbers = design?.syllabus?.lessons
    .filter((l) => l.lesson_type === 'instructional')
    .map((l) => l.lesson_number) ?? [];

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <button onClick={onBack} className="text-sm text-blue-600 hover:underline">← Courses</button>
          <h2 className="text-lg font-semibold text-gray-900 mt-1">{courseTitle}</h2>
          <div className="text-xs text-gray-500">
            Design v{run.design_version} · ${totalCost.toFixed(2)} · updated{' '}
            {new Date(run.updatedAt).toLocaleString()}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <TargetIcons run={run} onOpenRender={onOpenRender} />
          <Chip tone={status.tone}>{status.label}{run.design_status === 'approved' ? ` v${run.approved_version}` : ''}</Chip>
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">{error}</div>}
      {run.last_error && run.design_status === 'design_failed' && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">{run.last_error}</div>
      )}
      {issues.length > 0 && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
          <div className="font-medium mb-1">Validation issues:</div>
          <ul className="list-disc ml-4 space-y-0.5">
            {issues.map((i) => {
              const word = vocabIssueWord(i);
              return (
                <li key={i}>
                  {i}
                  {word && (
                    <button disabled={busy}
                      className="ml-2 px-1.5 py-0.5 text-xs rounded border border-amber-400 bg-white text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                      onClick={() => allowWord(word)}>
                      Allow “{word}” for this course
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {isDesigning && <DesigningProgress design={doc?.design ?? null} />}

      {!isDesigning && (
        <div className="flex gap-2 flex-wrap">
          {run.design_status === 'design_review' && !editing && (
            <div className="relative flex">
              <button disabled={busy}
                onClick={() => act(async () => {
                  const result = await designRunsService.approve(runId);
                  if (result.issues?.length) {
                    setIssues(result.issues);
                    throw new Error('The design has validation issues — fix them (or allow flagged words) and approve again.');
                  }
                })}
                className="px-2.5 py-1.5 rounded-l-md text-sm bg-green-600 hover:bg-green-700 text-white disabled:opacity-50">
                Approve design v{run.design_version}
              </button>
              <button disabled={busy} onClick={() => setApproveMenu(!approveMenu)}
                className="px-1.5 py-1.5 rounded-r-md text-sm bg-green-700 hover:bg-green-800 text-white border-l border-green-500 disabled:opacity-50">
                ▾
              </button>
              {approveMenu && (
                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                  {(Object.keys(TARGET_META) as RealizationTarget[]).map((t) => (
                    <button key={t} className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 whitespace-nowrap"
                      onClick={() => {
                        setApproveMenu(false);
                        act(async () => {
                          await designRunsService.approve(runId);
                          await designRunsService.realize(runId, t);
                          onOpenRender();
                        });
                      }}>
                      Approve & render for {TARGET_META[t].name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {run.design_status === 'approved' && !editing && (
            <SmallButton tone="primary" disabled={busy} onClick={onOpenRender}>
              Render…
            </SmallButton>
          )}
          {!editing && run.design_status !== 'design_failed' && (
            <SmallButton disabled={busy} onClick={() => { setDraft(doc?.design ?? null); setEditing(true); setTab('overview'); }}>
              Edit design
            </SmallButton>
          )}
          {run.design_status === 'design_failed' && (
            <SmallButton disabled={busy} onClick={() => act(() => designRunsService.rerunDesign(runId))}>
              Retry design
            </SmallButton>
          )}
          {editing && (
            <>
              <SmallButton tone="primary" disabled={busy} onClick={saveDraft}>
                Save (becomes v{run.design_version + 1}, needs re-approval)
              </SmallButton>
              <SmallButton disabled={busy}
                onClick={() => { setEditing(false); setDraft(doc?.design ?? null); setIssues([]); }}>
                Cancel
              </SmallButton>
            </>
          )}
        </div>
      )}

      {!isDesigning && doc && (
        <div>
          <div className="flex gap-1 mb-3">
            {([['brief', 'Brief'], ['overview', 'Overview'], ['lessons', 'Lessons'], ['media', 'Scripts & media']] as [Tab, string][])
              .map(([t, label]) => (
                <button key={t} onClick={() => setTab(t)}
                  className={`px-3 py-1.5 text-sm rounded-md ${tab === t ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}>
                  {label}
                </button>
              ))}
          </div>
          {tab === 'brief' && (
            <BriefTab runId={runId} scope={doc.design.scope} busy={busy || editing}
              busyReason={editing
                ? 'Finish or cancel the design edit first (Save/Cancel above)'
                : undefined}
              onRedesigned={refresh} setError={setError} />
          )}
          {tab === 'overview' && design && (
            <div className="max-w-4xl">
              <OverviewTab design={design} editing={editing} onChange={setDraft} />
            </div>
          )}
          {tab === 'lessons' && design && (
            <div className="space-y-4 max-w-4xl">
              {lessonNumbers.map((n) => (
                <DossierLesson key={n} design={design} lessonNumber={n} editing={editing}
                  onChange={setDraft} />
              ))}
            </div>
          )}
          {tab === 'media' && design && (
            <div className="max-w-4xl">
              <DossierAssets design={design} editing={editing} onChange={setDraft} />
            </div>
          )}
        </div>
      )}

    </div>
  );
}
